/**
 * Identity tab list query — PARTY_MODEL_V1 Phase 2 / decision D6a.
 *
 * Default view is the Party family (legal entity · natural person · brand).
 * Units, places, and content stay reachable via "Registry, all kinds".
 * Drafts are in the default status set (all). Never fabricate rows.
 */

export const IDENTITY_KIND_FILTER_LABEL = 'Kind';
export const IDENTITY_KIND_FILTER_CONTROL_ID = 'identity-kind-filter';

export const IDENTITY_KIND_VIEW_PARTY = 'party' as const;
export const IDENTITY_KIND_VIEW_ALL = 'all' as const;

export type IdentityKindView = typeof IDENTITY_KIND_VIEW_PARTY | typeof IDENTITY_KIND_VIEW_ALL;

/** Secondary view label — exact string required by D6a. */
export const IDENTITY_ALL_KINDS_VIEW_LABEL = 'Registry, all kinds';
export const IDENTITY_PARTY_VIEW_LABEL = 'Party (legal entity · natural person · brand)';

export const IDENTITY_DEFAULT_KIND_VIEW: IdentityKindView = IDENTITY_KIND_VIEW_PARTY;
export const IDENTITY_DEFAULT_STATUS = 'all' as const;

export const PARTY_FAMILY_LEAVES = ['legal_entity', 'natural_person', 'brand'] as const;

/** Live Trekker fixtures named in D6a — cite, do not mint. */
export const MOKORO_LEGAL_ENTITY_ID = '11e1a627-d967-422c-9fca-b42f8e24d326';
export const FORD_UTILITY_TRACTOR_UNIT_ID = 'f432aeed-f4fb-41ec-bfb5-593cdcb06a2d';

const NON_PARTY_KINDS = new Set([
  'built-artifact',
  'built_artifact',
  'place',
  'content',
  'organism',
  'specification/class',
  'specification',
  'abstract',
]);

const NON_PARTY_TEMPLATE_SEGMENTS = new Set([
  'built_artifact',
  'place',
  'content',
  'organism',
  'spec',
  'products',
  'digital_assets',
  'abstract',
]);

const NON_PARTY_LEAVES = new Set([
  'utility_tractor_unit',
  'utility_tractor',
  'equipment',
  'device',
  'asset',
]);

const PARTY_ENTITY_TYPE_EXACT = new Set([
  'party',
  'legal_entity',
  'legal-entity',
  'legalentity',
  'natural_person',
  'natural-person',
  'brand',
  'company',
  'individual',
  'person',
]);

export const IDENTITY_KIND_FILTER_OPTIONS: Array<{ value: IdentityKindView; label: string }> = [
  { value: IDENTITY_KIND_VIEW_PARTY, label: IDENTITY_PARTY_VIEW_LABEL },
  { value: IDENTITY_KIND_VIEW_ALL, label: IDENTITY_ALL_KINDS_VIEW_LABEL },
];

export type IdentityListRow = {
  id?: string;
  name?: string;
  status?: string;
  kind?: string | null;
  leaf?: string | null;
  class?: string | null;
  entity_type?: string | null;
  entity_subtype?: string | null;
  template_id?: string | null;
  attributes?: Record<string, unknown> | null;
};

export type IdentityListQuery = {
  kindView: IdentityKindView;
  status: string;
};

export function defaultIdentityListQuery(): IdentityListQuery {
  return {
    kindView: IDENTITY_DEFAULT_KIND_VIEW,
    status: IDENTITY_DEFAULT_STATUS,
  };
}

/** Params sent to GET /entity/entities. status=all is omitted so drafts are not hidden. */
export function buildIdentityListRequestParams(query: IdentityListQuery): {
  kind?: string;
  status?: string;
} {
  const params: { kind?: string; status?: string } = {};
  if (query.kindView === IDENTITY_KIND_VIEW_PARTY) {
    params.kind = 'Party';
  }
  const status = String(query.status || '').trim();
  if (status && status !== 'all') {
    params.status = status;
  }
  return params;
}

export function identityKindFilterControlExists(options = IDENTITY_KIND_FILTER_OPTIONS): boolean {
  return options.some((o) => o.value === IDENTITY_KIND_VIEW_PARTY)
    && options.some((o) => o.label === IDENTITY_ALL_KINDS_VIEW_LABEL);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function norm(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function templateSegment(templateId: unknown): string {
  const tid = String(templateId ?? '').trim();
  if (!tid.startsWith('template:')) return '';
  return tid.split(':')[1] || '';
}

function templateLeaf(templateId: unknown): string {
  const tid = String(templateId ?? '').trim();
  if (!tid.startsWith('template:')) return '';
  const parts = tid.split(':');
  return parts[parts.length - 1] || '';
}

function entityTypeLeaf(entityType: unknown): string {
  const raw = String(entityType ?? '').trim();
  if (!raw) return '';
  const kindPath = raw.match(/^kind=([^/]+)/i);
  if (kindPath) {
    const rest = raw.split('/');
    return rest[rest.length - 1] || kindPath[1];
  }
  const parts = raw.split(':');
  return parts[parts.length - 1] || raw;
}

function rowKind(row: IdentityListRow): string {
  const explicit = String(row.kind ?? '').trim();
  if (explicit) return explicit;
  const et = String(row.entity_type ?? '');
  const kindPath = et.match(/^kind=([^/]+)/);
  if (kindPath) return kindPath[1];
  const seg = templateSegment(row.template_id);
  if (seg === 'party') return 'Party';
  if (seg === 'built_artifact') return 'Built-artifact';
  if (seg === 'place') return 'Place';
  if (seg === 'content' || seg === 'digital_assets') return 'Content';
  return '';
}

function rowLeaf(row: IdentityListRow): string {
  const attrs = asRecord(row.attributes) || {};
  const candidates = [
    row.leaf,
    row.class,
    attrs.leaf,
    attrs.class,
    attrs.type_key,
    templateLeaf(row.template_id),
    entityTypeLeaf(row.entity_type),
    row.entity_subtype,
  ];
  for (const c of candidates) {
    const n = norm(c);
    if (n) return n;
  }
  return '';
}

function isNonPartyKindName(kind: string): boolean {
  const n = norm(kind).replace(/\s+/g, '-');
  return NON_PARTY_KINDS.has(n) || NON_PARTY_KINDS.has(n.replace(/-/g, '_'));
}

export function isPartyFamilyRow(row: IdentityListRow | null | undefined): boolean {
  if (!row || typeof row !== 'object') return false;
  const kind = rowKind(row);
  if (kind && isNonPartyKindName(kind)) return false;

  const seg = norm(templateSegment(row.template_id));
  if (seg && NON_PARTY_TEMPLATE_SEGMENTS.has(seg)) return false;

  const leaf = rowLeaf(row);
  if (leaf && NON_PARTY_LEAVES.has(leaf)) return false;

  if (norm(kind) === 'party') return true;
  if (seg === 'party') return true;
  if ((PARTY_FAMILY_LEAVES as readonly string[]).includes(leaf)) return true;

  const et = norm(row.entity_type);
  if (!et) return false;
  if (et.startsWith('kind=party')) return true;
  if (et.startsWith('legal:') || et.startsWith('party:')) return true;
  const short = et.replace(/^legal:/, '').replace(/-/g, '_');
  return PARTY_ENTITY_TYPE_EXACT.has(et) || PARTY_ENTITY_TYPE_EXACT.has(short);
}

export function applyIdentityListFilters<T extends IdentityListRow>(
  rows: readonly T[] | null | undefined,
  query: IdentityListQuery = defaultIdentityListQuery(),
): T[] {
  if (!Array.isArray(rows)) return [];
  const status = String(query.status || IDENTITY_DEFAULT_STATUS).trim() || IDENTITY_DEFAULT_STATUS;
  return rows.filter((row) => {
    if (!row || typeof row !== 'object') return false;
    if (query.kindView !== IDENTITY_KIND_VIEW_ALL && !isPartyFamilyRow(row)) return false;
    if (status !== 'all' && norm(row.status) !== norm(status)) return false;
    return true;
  });
}

export function identityListEmptyCopy(kindView: IdentityKindView, rowCount: number): string {
  if (rowCount > 0) return '';
  if (kindView === IDENTITY_KIND_VIEW_PARTY) {
    return 'No parties in this view.';
  }
  return 'No registry records in this view.';
}

export function rowStatusChip(status: unknown): { label: string; tone: string } | null {
  const label = String(status ?? '').trim();
  if (!label) return null;
  return { label, tone: label };
}
